import { RequestedDate } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import {
  parseTimeString,
  parseISOTimeString,
} from '../../../../utils/timeStringParser'
import { formatDateToMMDDYYYY } from '../../../../utils/date'
import { getOtherTypeDisplay } from '../../../../utils/requestTypeUtils'

export interface TableRow {
  id: string
  toReqDtlId?: number
  requestType: string
  date: string
  day: string
  times: string
  requestedHours: string
  hoursInOtherRequests: string
  _otherRequestHoursTotal?: number
}

/**
 * Sorts Current Request / Review table rows by their `date` (MM/DD/YYYY)
 * ascending. Rows with no parseable date (e.g. the synthetic "Total Hours"
 * row, whose `date` is '') sort to the end rather than being dropped, so
 * appending a total row after calling this still lands it last.
 *
 * Shared by buildTableRows (initial load from the API, which does not come
 * pre-sorted) and DraftView's local merges (resync effect + "add date to
 * request" review flow) so the table reads chronologically regardless of API
 * order or the order dates were added/edited in during the session.
 */
export const sortRowsByDate = <T extends { date?: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => {
    const aTime = a.date ? new Date(a.date).getTime() : Number.NaN
    const bTime = b.date ? new Date(b.date).getTime() : Number.NaN
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0
    if (Number.isNaN(aTime)) return 1
    if (Number.isNaN(bTime)) return -1
    return aTime - bTime
  })

export interface OtherRequestAllocation {
  hours?: number
  toTypeCd?: string
}

export interface OtherRequestEntry {
  toReqId?: string
  status?: string
  toReqDtlId?: number
}

export interface OtherRequestDetail {
  toReqDtlId?: number
  toReqId?: string | number
  status?: string
  timeOffAllocations?: OtherRequestAllocation[]
  otherRequests?: OtherRequestEntry[]
}

export interface OtherRequestBreakdownEntry {
  typeAbbreviation: string
  hours: number
}

const REQUEST_TYPE_MAP: Record<string, string> = {
  V: 'Vacation (PTO/ETO)',
  F: 'Float',
  D: 'FPD (Flexible Personal Day)',
  B: 'Birthday',
  A: 'Vacation Bidding',
  O: 'Other',
  M: 'Multiple (Combined)',
}

export const TABLE_COLUMNS = [
  { key: 'requestType', label: 'Request Type' },
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'times', label: 'Time' },
  { key: 'requestedHours', label: 'Requested Hours' },
  { key: 'hoursInOtherRequests', label: 'Hours in Other Requests' },
]

export const TABLE_COLUMNS_WITH_ACTIONS = [
  ...TABLE_COLUMNS,
  { key: 'edit', label: 'Edit' },
  { key: 'remove', label: 'Remove' },
]

export const RETRACT_TABLE_COLUMNS = [
  { key: 'retract', label: 'Retract' },
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'times', label: 'Time' },
  { key: 'requestedHours', label: 'Hours' },
]

export const normalizeStatus = (status: string) => {
  const normalized = (status || '').toLowerCase().trim()

  // Backend payloads have historically emitted both *_release and *_released.
  // Canonicalize to *_released so downstream routing only needs one code path.
  if (normalized === 'resubmitted_release') return 'resubmitted_released'

  return normalized
}

/**
 * Statuses for which the "Hours in Other Requests" column is meaningless —
 * the current request is no longer active, so its dates shouldn't show
 * overlap hours from other requests. The Current Request table blanks out
 * each row's cell and reports a 0.00 total for these statuses.
 */
const HIDE_OTHER_REQUEST_HOURS_STATUSES = new Set([
  'withdrawn',
  'denied',
  'pdenied',
  'retracted',
])

/**
 * Resolve the request-type label for a single day row.
 *
 * A day can carry several allocations. When it has more than one DISTINCT
 * allocation type (e.g. Vacation + Float) we show the combined label, matching
 * the live create/edit flow's `determineRequestType`. Previously only the first
 * allocation's type code was mapped, so a mixed-type day incorrectly displayed
 * as "Vacation". A single distinct type maps through REQUEST_TYPE_MAP as before.
 *
 * "Other" ('O') is a special case: legacy/the live create flow always show
 * the specific subtype (e.g. "Other - Union Business"), never the bare
 * "Other" label, when one was selected. Without this, a saved Other-type day
 * showed just "Other" here even though `getOtherTypeDisplay` (used by the
 * live form's `determineRequestType`) would have shown the full subtype
 * label for the exact same data.
 *
 * @param d - The requested-date row, including its raw `timeOffAllocations`.
 * @returns The display label for the row's Request Type column.
 */
const resolveRowRequestType = (d: RequestedDate): string => {
  const distinctTypes = new Set(
    (d.timeOffAllocations ?? []).map((a) => a.toTypeCd).filter(Boolean)
  )
  if (distinctTypes.size > 1) return REQUEST_TYPE_MAP.M

  const requestTypeCode = d.requestType ?? ''
  if (requestTypeCode.toUpperCase() === 'O') {
    const otherAlloc = (d.timeOffAllocations ?? []).find(
      (a) => String(a.toTypeCd).toUpperCase() === 'O' && a.toSubTypeCd
    )
    const subType = otherAlloc?.toSubTypeCd ?? d.toSubTypeCd ?? null
    if (subType) return getOtherTypeDisplay(subType)
  }

  return REQUEST_TYPE_MAP[requestTypeCode] ?? requestTypeCode
}

const BREAKDOWN_DISPLAY_ORDER = ['Vac', 'Float', 'FPD', 'Bday', 'Oth', 'Multi', 'Bid'] as const

/**
 * Maps a raw time-off type code to the same short abbreviation legacy uses
 * for "Hours in Other Requests" (seeded from the TORT_PROPERTY table there:
 * Vac, Float, FPD, Bday, Multi, Oth, Bid — Bid is its own entry there, distinct
 * from Oth, for a Vacation Bid request showing up as an existing request on
 * someone's date). Anything unrecognized falls back to "Oth", matching
 * legacy's default rather than surfacing the raw code.
 */
export const typeCodeToAbbreviation = (typeCode?: string): string => {
  const normalized = (typeCode ?? '').toUpperCase()
  if (normalized === 'V' || normalized === 'PTO' || normalized === 'ETO') return 'Vac'
  if (normalized === 'F') return 'Float'
  if (normalized === 'D' || normalized === 'X') return 'FPD'
  if (normalized === 'B') return 'Bday'
  if (normalized === 'M') return 'Multi'
  if (normalized === 'A') return 'Bid'
  return 'Oth'
}

const sortBreakdownEntries = (
  entries: OtherRequestBreakdownEntry[]
): OtherRequestBreakdownEntry[] => {
  const priority = new Map<string, number>(
    BREAKDOWN_DISPLAY_ORDER.map((key, index) => [key, index])
  )
  return [...entries].sort((a, b) => {
    const aPriority = priority.get(a.typeAbbreviation) ?? Number.MAX_SAFE_INTEGER
    const bPriority = priority.get(b.typeAbbreviation) ?? Number.MAX_SAFE_INTEGER
    if (aPriority !== bPriority) return aPriority - bPriority
    return a.typeAbbreviation.localeCompare(b.typeAbbreviation)
  })
}

const toBreakdownMap = (
  detailsArray: OtherRequestDetail[]
): Map<string, number> => {
  const totalsByType = new Map<string, number>()
  detailsArray.forEach((detail) => {
    ;(detail.timeOffAllocations ?? []).forEach((allocation) => {
      const hours = Number(allocation?.hours) || 0
      if (hours <= 0) return
      const abbreviation = typeCodeToAbbreviation(allocation?.toTypeCd)
      totalsByType.set(abbreviation, (totalsByType.get(abbreviation) ?? 0) + hours)
    })
  })
  return totalsByType
}

const subtractCurrentRowHours = (
  totalsByType: Map<string, number>,
  requestedDate: RequestedDate
): Map<string, number> => {
  const adjustedTotals = new Map(totalsByType)
  let remainingHoursToSubtract = Number(requestedDate.requestedHours ?? 0) || 0

  // Prefer typed subtraction when row allocations are available.
  ;(requestedDate.timeOffAllocations ?? []).forEach((allocation) => {
    if (remainingHoursToSubtract <= 0) return
    const allocationHours = Number(allocation?.hours) || 0
    if (allocationHours <= 0) return

    const abbreviation = typeCodeToAbbreviation(allocation?.toTypeCd)
    const currentBucket = adjustedTotals.get(abbreviation) ?? 0
    if (currentBucket <= 0) return

    const amountToSubtract = Math.min(allocationHours, currentBucket, remainingHoursToSubtract)
    const nextValue = currentBucket - amountToSubtract
    if (nextValue > 0) {
      adjustedTotals.set(abbreviation, nextValue)
    } else {
      adjustedTotals.delete(abbreviation)
    }
    remainingHoursToSubtract -= amountToSubtract
  })

  // Preserve legacy behavior when type-level subtraction cannot fully consume
  // requested hours by subtracting residual from remaining buckets.
  if (remainingHoursToSubtract > 0) {
    const orderedEntries = sortBreakdownEntries(
      Array.from(adjustedTotals.entries()).map(([typeAbbreviation, hours]) => ({
        typeAbbreviation,
        hours,
      }))
    )

    orderedEntries.forEach((entry) => {
      if (remainingHoursToSubtract <= 0) return
      const currentBucket = adjustedTotals.get(entry.typeAbbreviation) ?? 0
      if (currentBucket <= 0) return

      const amountToSubtract = Math.min(currentBucket, remainingHoursToSubtract)
      const nextValue = currentBucket - amountToSubtract
      if (nextValue > 0) {
        adjustedTotals.set(entry.typeAbbreviation, nextValue)
      } else {
        adjustedTotals.delete(entry.typeAbbreviation)
      }
      remainingHoursToSubtract -= amountToSubtract
    })
  }

  return adjustedTotals
}

const toBreakdownEntries = (
  totalsByType: Map<string, number>
): OtherRequestBreakdownEntry[] =>
  sortBreakdownEntries(
    Array.from(totalsByType.entries())
      .map(([typeAbbreviation, hours]) => ({
        typeAbbreviation,
        hours,
      }))
      .filter((entry) => entry.hours > 0)
  )

export const formatBreakdown = (entries: OtherRequestBreakdownEntry[]): string => {
  if (entries.length === 0) return '0.00'
  return entries
    .map((entry) => `${entry.hours.toFixed(2)} ${entry.typeAbbreviation}`)
    .join(' / ')
}

/**
 * Sums a "Hours in Other Requests" display string back into a number,
 * e.g. "3.00 Oth / 2.00 FPD" -> 5. Splitting on " / " first (rather than
 * calling parseFloat directly on the whole string) is required — parseFloat
 * only reads the first numeric token, silently dropping every segment past
 * the first "/" and undercounting any multi-type day's total.
 */
export const getDisplayedOtherHoursTotal = (displayValue: string): number => {
  if (!displayValue) return 0

  return displayValue
    .split(' / ')
    .reduce((sum, segment) => sum + (Number.parseFloat(segment) || 0), 0)
}

/**
 * Builds one breakdown entry per EXISTING request in detailsArray — matching
 * legacy's getShortDescriptionForDetailObject rule exactly: "Multi" only
 * collapses the allocations WITHIN a single existing request (i.e. one
 * request that itself spans more than one leave type on that date), never
 * across separate requests. Two separate single-type requests on the same
 * date stay as two separate entries.
 */
export const toPerRequestBreakdownEntries = (
  detailsArray: OtherRequestDetail[]
): OtherRequestBreakdownEntry[] => {
  return detailsArray
    .map((detail) => {
      const validAllocations = (detail.timeOffAllocations ?? []).filter((allocation) => {
        return !!allocation?.toTypeCd && (Number(allocation?.hours) || 0) > 0
      })

      if (validAllocations.length === 0) return null

      const totalHours = validAllocations.reduce(
        (sum, allocation) => sum + (Number(allocation?.hours) || 0),
        0
      )
      const typeAbbreviation =
        validAllocations.length > 1
          ? 'Multi'
          : typeCodeToAbbreviation(validAllocations[0].toTypeCd)

      return {
        typeAbbreviation,
        hours: totalHours,
      }
    })
    .filter((entry): entry is OtherRequestBreakdownEntry => {
      return entry !== null && entry.hours > 0
    })
}

export const buildTableRows = (
  requestedDates: RequestedDate[] | undefined,
  otherReqDetails?: Record<string, OtherRequestDetail[]>,
  requestStatus?: string

): TableRow[] => {
  if (!requestedDates || requestedDates.length === 0) return []

  const hideOtherRequestHours = HIDE_OTHER_REQUEST_HOURS_STATUSES.has(
    normalizeStatus(requestStatus ?? '')
  )

  const calculateHoursInOtherRequests = (d: RequestedDate): {
    displayValue: string
    numericTotal: number
  } => {
    // Inactive-request statuses (withdrawn/denied/pdenied/retracted) never
    // show overlap hours from other requests — blank the cell rather than
    // computing a breakdown that would otherwise still be displayed.
    if (hideOtherRequestHours) {
      return { displayValue: '0.00', numericTotal: 0 }
    }

    const dateKey = d.date ?? ''
    const fallbackValue = Math.max(Number(d.hoursInOtherRequests ?? 0) || 0, 0)
    const detailsArray = otherReqDetails?.[dateKey]

    if (!Array.isArray(detailsArray) || detailsArray.length === 0) {
      if (fallbackValue <= 0) {
        return {
          displayValue: '0.00',
          numericTotal: 0,
        }
      }
      return {
        displayValue: `${fallbackValue.toFixed(2)} Oth`,
        numericTotal: fallbackValue,
      }
    }

    const currentDetailId = d.toReqDtlId
    const normalizedCurrentDetailId =
      typeof currentDetailId === 'number' ? currentDetailId : Number(currentDetailId)
    const hasCurrentDetailMatch =
      Number.isFinite(normalizedCurrentDetailId) &&
      detailsArray.some((detail) => Number(detail?.toReqDtlId) === normalizedCurrentDetailId)

    if (hasCurrentDetailMatch) {
      const otherDetailsForSameDate = detailsArray.filter(
        (detail) => Number(detail?.toReqDtlId) !== normalizedCurrentDetailId
      )
      const otherPerRequestEntries = toPerRequestBreakdownEntries(otherDetailsForSameDate)

      if (otherPerRequestEntries.length === 0) {
        return {
          displayValue: '0.00',
          numericTotal: 0,
        }
      }

      const otherNumericTotal = otherPerRequestEntries.reduce(
        (sum, entry) => sum + entry.hours,
        0
      )

      if (otherNumericTotal <= 0) {
        return {
          displayValue: '0.00',
          numericTotal: 0,
        }
      }

      return {
        displayValue: formatBreakdown(otherPerRequestEntries),
        numericTotal: otherNumericTotal,
      }
    }

    const totalsByType = toBreakdownMap(detailsArray)
    if (totalsByType.size === 0) {
      return {
        displayValue: '0.00',
        numericTotal: 0,
      }
    }

    const remainingTotalsByType = subtractCurrentRowHours(totalsByType, d)
    const breakdownEntries = toBreakdownEntries(remainingTotalsByType)
    const numericTotal = breakdownEntries.reduce(
      (sum, entry) => sum + entry.hours,
      0
    )

    if (numericTotal <= 0) {
      return {
        displayValue: '0.00',
        numericTotal: 0,
      }
    }

    return {
      displayValue: formatBreakdown(breakdownEntries),
      numericTotal,
    }
  }

  const formatTimes = (d: RequestedDate): string => {
    // If times is "Full Day", return as is
    if (d.times === 'Full Day') return 'Full Day'

    // Try to parse d.times (could be ISO format or already formatted)
    if (d.times && d.times !== 'Full Day') {
      const parsed = parseTimeString(d.times)
      if (parsed) {
        return `${parsed.from.hour}:${parsed.from.minute} ${parsed.from.ampm} - ${parsed.to.hour}:${parsed.to.minute} ${parsed.to.ampm}`
      }
    }

    // Fallback: try parsing from startTime and endTime if available
    if (d.startTime && d.endTime) {
      const startTimeStr = parseISOTimeString(d.startTime)
      const endTimeStr = parseISOTimeString(d.endTime)
      if (startTimeStr && endTimeStr) {
        return `${startTimeStr} - ${endTimeStr}`
      }
    }

    // Fallback to raw times or empty string
    return d.times ?? ''
  }

  const rows = requestedDates.map((d, i) => {
    const { displayValue, numericTotal } = calculateHoursInOtherRequests(d)
    return {
      id: `row-${d.toReqDtlId ?? i}`,
      toReqDtlId: d.toReqDtlId,
      requestType: resolveRowRequestType(d),
      date: formatDateToMMDDYYYY(d.date ?? ''),
      day: d.day ?? '',
      times: formatTimes(d),
      requestedHours: Number(d.requestedHours ?? 0),
      hoursInOtherRequests: displayValue,
      _otherRequestHoursTotal: numericTotal,
    }
  })
  // API order is not guaranteed to be chronological — sort so the Current
  // Request table always reads in ascending date order.
  const sortedRows = sortRowsByDate(rows)

  const totalRequested = sortedRows.reduce(
    (sum, r) =>
      sum +
      (typeof r.requestedHours === 'number'
        ? r.requestedHours
        : Number(r.requestedHours || 0)),
    0
  )
  const totalOther = sortedRows.reduce(
    (sum, r) => sum + getDisplayedOtherHoursTotal(r.hoursInOtherRequests),
    0
  )

  // Format rows for display — birthday rows show 'NA' instead of hours
  const formattedRows = sortedRows.map((row) => ({
    ...row,
    requestedHours: row.requestType === 'Birthday' ? 'NA' : row.requestedHours.toFixed(2),
  }))

  return [
    ...formattedRows,
    {
      id: 'total',
      requestType: 'Total Hours',
      date: '',
      day: '',
      times: '',
      requestedHours: totalRequested.toFixed(2),
      hoursInOtherRequests: totalOther.toFixed(2),
      _totalRow: true,
      _rowStyle: {
        backgroundColor: '#D3D6DC',
        border: '1px solid #878F9B',
      },
    } as any,
  ]
}

/**
 * Normalizes a date string (either YYYY-MM-DD or MM/DD/YYYY) to YYYY-MM-DD
 * for looking up otherReqDetails, using plain string parsing rather than a
 * Date object — a Date-based conversion here would parse YYYY-MM-DD as UTC
 * midnight and MM/DD/YYYY as local midnight, silently shifting the date by
 * a day in non-UTC timezones.
 */
const toDateKey = (dateStr: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
  }
  return dateStr
}

/**
 * Matches legacy's "Day in Other Request(s)" checkbox for Vacation Bidding
 * (torCurrent.js: getOverlappedDayVBCheckBoxDetailObject, driven by
 * TimeOffRequestDelegate's toReqDetailsParsed/submittedTOReqDetails). Legacy
 * populates that data via findTOReqForGivenMonthAndYear(nuid, month, year) —
 * which has no request-type filter — so the checkbox is checked whenever
 * ANY existing request (any type, not just another Vacation Bid) already
 * has a date on that day, not only when the other request is itself a
 * Vacation Bid. Legacy renders this as a checked/unchecked checkbox, never
 * a numeric hours value.
 *
 * `excludeRequestId` filters out entries belonging to the request currently
 * being edited/viewed. When editing an already-persisted request (draft,
 * returned, on-hold) that request's own dates come back in `otherReqData`
 * too, since it fetches every request for the nuid/month/year — without this
 * exclusion, a request's own dates would always match themselves and the
 * checkbox would show checked even with no actual overlap from another
 * request.
 */
export const isVacationBidOverlapForDate = (
  otherReqData: Record<string, OtherRequestDetail[]> | null | undefined,
  dateStr: string,
  excludeRequestId?: string | number | null
): boolean => {
  const details = otherReqData?.[toDateKey(dateStr)]
  if (!Array.isArray(details)) return false
  const otherRequestsOnly =
    excludeRequestId === undefined || excludeRequestId === null || excludeRequestId === ''
      ? details
      : details.filter((detail) => String(detail.toReqId ?? '') !== String(excludeRequestId))
  return otherRequestsOnly.some((detail) =>
    (detail.timeOffAllocations ?? []).some(
      (allocation) => !!allocation?.toTypeCd && (Number(allocation?.hours) || 0) > 0
    )
  )
}

/** Statuses that indicate an active (non-draft, non-returned) time-off request. */
const VAC_BID_ACTIVE_STATUSES = new Set([
  'SUBMITTED',
  'RESUBMITTED',
  'RESUBMITTED_ONHOLD',
  'RESUBMITTED_RELEASED',
  'PRTSUBMITTED',
  'APPROVED_PRTSUBMITTED',
  'PRTSUBMITTED_ONHOLD',
  'PRTSUBMITTED_RELEASED',
  'SUBMITTED_ONHOLD',
  'SUBMITTED_RELEASED',
])


export const isVacBidDayInOtherRequestsChecked = (
  otherReqData: Record<string, OtherRequestDetail[]> | null | undefined,
  dateStr: string,
  toReqDtlId?: number | null
): boolean => {
  const entries = otherReqData?.[toDateKey(dateStr)]
  if (!Array.isArray(entries) || entries.length === 0) {
    return false
  }

  const hasActiveStatus = (r: OtherRequestEntry): boolean =>
    VAC_BID_ACTIVE_STATUSES.has((r.status ?? '').toUpperCase())

  const hasActiveDetailStatus = (e: OtherRequestDetail): boolean =>
    VAC_BID_ACTIVE_STATUSES.has((e.status ?? '').toUpperCase())

  if (toReqDtlId != null) {
    const myEntry = entries.find((e) => Number(e.toReqDtlId) === toReqDtlId)

    if (myEntry) {
      // Our detail is a top-level entry: check its otherRequests, filtering self-references
      const raw = myEntry.otherRequests ?? []
      const filtered = raw.filter((r) => Number(r.toReqDtlId) !== toReqDtlId)
      const result = filtered.some(hasActiveStatus)
      return result
    }

    // Our detail is only referenced inside another entry's otherRequests (not top-level).
    // Those referencing entries ARE the overlapping requests — check their own active status.
    const referencingEntries = entries.filter((e) =>
      (e.otherRequests ?? []).some((r) => Number(r.toReqDtlId) === toReqDtlId)
    )
    const result = referencingEntries.some(hasActiveDetailStatus)
    return result
  }

  // Create flow (no toReqDtlId yet): check otherRequests across all entries
  const result = entries.some((entry) => (entry.otherRequests ?? []).some(hasActiveStatus))
  return result
}